import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Activity, AlertTriangle, CheckCircle2, ClipboardList, Database, Eye, Search } from 'lucide-react';
import { channelApi } from '../config/api';
import { useAuth } from '../context/AuthContext';

const badgeTone = {
  SYNCED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  LIVE: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  ISSUE: 'bg-red-50 text-red-700 border-red-200',
  NOT_STARTED: 'bg-slate-50 text-slate-600 border-slate-200',
  CONFIGURATION: 'bg-amber-50 text-amber-700 border-amber-200',
  CONNECTIVITY: 'bg-amber-50 text-amber-700 border-amber-200',
  MAPPING: 'bg-amber-50 text-amber-700 border-amber-200',
  STAGING_TEST: 'bg-blue-50 text-blue-700 border-blue-200',
  ON_HOLD: 'bg-slate-100 text-slate-700 border-slate-200',
};

function normalizeAdminStatus(user) {
  return String(user?.admin_status || user?.adminStatus || user?.role || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
}

function fmtDate(value) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function StatusBadge({ value }) {
  const status = value || 'NOT_STARTED';
  return (
    <span className={`inline-flex rounded-md border px-2 py-1 text-[11px] font-bold ${badgeTone[status] || badgeTone.NOT_STARTED}`}>
      {status}
    </span>
  );
}

function SummaryCard({ title, value, Icon }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{title}</div>
          <div className="mt-2 text-2xl font-bold text-slate-900">{value ?? '-'}</div>
        </div>
        <Icon className="h-5 w-5 text-slate-500" />
      </div>
    </div>
  );
}

function CodeBlock({ value }) {
  if (!value) return <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-500">No sample contract documented yet.</div>;
  return (
    <pre className="overflow-auto rounded-lg bg-slate-950 p-4 text-xs text-slate-50">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

function IntegrationList({ integrations, summary, loading, onView }) {
  const [query, setQuery] = React.useState('');
  const [status, setStatus] = React.useState('ALL');
  const [transport, setTransport] = React.useState('ALL');

  const filtered = React.useMemo(() => {
    const needle = query.trim().toLowerCase();
    return integrations.filter((item) => {
      const haystack = [item.member?.code, item.member?.name, item.transport, item.adapter, item.status].filter(Boolean).join(' ').toLowerCase();
      const searchOk = !needle || haystack.includes(needle);
      const statusOk = status === 'ALL'
        || (status === 'SYNCED_LIVE' && ['SYNCED', 'LIVE'].includes(item.status))
        || (status === 'PROCESS' && ['CONFIGURATION', 'CONNECTIVITY', 'MAPPING', 'STAGING_TEST'].includes(item.status))
        || item.status === status;
      const transportOk = transport === 'ALL' || String(item.transport || '').toLowerCase().includes(transport.toLowerCase());
      return searchOk && statusOk && transportOk;
    });
  }, [integrations, query, status, transport]);

  return (
    <div className="space-y-5">
      <div className="grid gap-3 md:grid-cols-4">
        <SummaryCard title="Member Terdaftar" value={summary?.members} Icon={Database} />
        <SummaryCard title="Sudah Sync" value={summary?.synced} Icon={CheckCircle2} />
        <SummaryCard title="Proses Integrasi" value={summary?.inProcess} Icon={Activity} />
        <SummaryCard title="Issue" value={summary?.issue} Icon={AlertTriangle} />
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <div className="relative md:col-span-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input className="h-10 w-full rounded-lg border border-slate-200 pl-9 pr-3 text-sm outline-none focus:border-slate-400" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cari Member..." />
        </div>
        <select className="h-10 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-slate-400" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="ALL">All Status</option>
          <option value="SYNCED_LIVE">Synced / Live</option>
          <option value="PROCESS">Integration Process</option>
          <option value="ISSUE">Issue</option>
          <option value="NOT_STARTED">Not Started</option>
        </select>
        <select className="h-10 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-slate-400" value={transport} onChange={(e) => setTransport(e.target.value)}>
          <option value="ALL">All Transport</option>
          <option value="REST">REST</option>
          <option value="Webhook">Webhook</option>
          <option value="FTP">FTP/SFTP</option>
          <option value="XML">XML</option>
        </select>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <table className="w-full min-w-[900px] text-left text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Member</th>
              <th className="px-4 py-3">Transport</th>
              <th className="px-4 py-3">Catalog</th>
              <th className="px-4 py-3">Purchase / Fulfillment</th>
              <th className="px-4 py-3">Products</th>
              <th className="px-4 py-3">Environment</th>
              <th className="px-4 py-3">Last Sync</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan="9" className="px-4 py-10 text-center text-slate-500">Memuat integrasi Member...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan="9" className="px-4 py-10 text-center text-slate-500">No member integration matches the current filters.</td></tr>
            ) : filtered.map((item) => (
              <tr key={item.member.code} className="align-top">
                <td className="px-4 py-3">
                  <div className="font-bold text-slate-900">{item.member.code}</div>
                  <div className="text-xs text-slate-500">{item.member.name}</div>
                </td>
                <td className="px-4 py-3 text-slate-700">{item.transport || '-'}</td>
                <td className="px-4 py-3">{item.capabilities.catalog ? 'Catalog OK' : 'Not available'}</td>
                <td className="px-4 py-3">{item.capabilities.purchase ? 'Purchase OK' : 'Not Supported'}</td>
                <td className="px-4 py-3">{item.stats.product_count} Products</td>
                <td className="px-4 py-3">{item.environment || '-'}</td>
                <td className="px-4 py-3">{fmtDate(item.timestamps.last_sync_at)}</td>
                <td className="px-4 py-3"><StatusBadge value={item.status} /></td>
                <td className="px-4 py-3">
                  <button type="button" onClick={() => onView(item.member.code)} className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                    <Eye className="h-3.5 w-3.5" /> View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DetailTabs({ detail, selectedProductCode }) {
  const tabs = ['Overview', 'Products', 'API Contract', 'Mapping', 'Configuration', 'Testing', 'Activity Log'];
  const [active, setActive] = React.useState(selectedProductCode ? 'Products' : 'Overview');
  const products = detail?.products || [];
  const selected = selectedProductCode && products.find((item) => item.code === selectedProductCode);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {tabs.map((tab) => (
          <button key={tab} type="button" onClick={() => setActive(tab)} className={`px-3 py-2 text-sm font-semibold ${active === tab ? 'border-b-2 border-[#12284A] text-[#12284A]' : 'text-slate-500 hover:text-slate-900'}`}>
            {tab}
          </button>
        ))}
      </div>

      {active === 'Overview' && (
        <div className="grid gap-3 md:grid-cols-3">
          {[
            ['Member', `${detail.integration.member.code} - ${detail.integration.member.name}`],
            ['Integration Status', detail.integration.status],
            ['Environment', detail.integration.environment],
            ['Transport', detail.integration.transport || '-'],
            ['Adapter', detail.integration.adapter || '-'],
            ['Product Count', detail.integration.stats.product_count],
            ['Mapped Products', detail.integration.stats.mapped_count],
            ['Unresolved Products', detail.integration.stats.unresolved_count],
            ['Last Sync', fmtDate(detail.integration.timestamps.last_sync_at)],
            ['Last Successful Sync', fmtDate(detail.integration.timestamps.last_success_at)],
            ['Last Error', '-'],
          ].map(([k, v]) => (
            <div key={k} className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{k}</div>
              <div className="mt-1 font-semibold text-slate-900">{v}</div>
            </div>
          ))}
        </div>
      )}

      {active === 'Products' && (
        <div className="space-y-4">
          {selected && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <div className="text-sm font-bold text-blue-900">PRIME Product</div>
              <div className="mt-2 grid gap-2 text-sm text-blue-900 md:grid-cols-3">
                <div><b>Code:</b> {selected.code}</div>
                <div><b>Member:</b> {selected.member?.code || '-'}</div>
                <div><b>Source:</b> {selected.resolverSource || selected.source}</div>
                <div><b>Mapping:</b> {selected.mappingStatus}</div>
                <div><b>Integration:</b> {selected.integration?.status || '-'}</div>
                <div><b>External Product ID:</b> {selected.metadata?.product_id || selected.metadata?.skuId || '-'}</div>
              </div>
            </div>
          )}
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                <tr><th className="px-4 py-3">Product Code</th><th className="px-4 py-3">Product Name</th><th className="px-4 py-3">Product Type</th><th className="px-4 py-3">Sync Status</th><th className="px-4 py-3">Mapping Status</th><th className="px-4 py-3">Last Sync</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {products.length === 0 ? (
                  <tr><td colSpan="6" className="px-4 py-10 text-center text-slate-500">No products synced yet.</td></tr>
                ) : products.map((product) => (
                  <tr key={`${product.source}-${product.id}`} className={product.code === selectedProductCode ? 'bg-blue-50/60' : ''}>
                    <td className="px-4 py-3 font-semibold">{product.code}</td>
                    <td className="px-4 py-3">{product.name}</td>
                    <td className="px-4 py-3">{product.productType || '-'}</td>
                    <td className="px-4 py-3"><StatusBadge value={product.syncStatus} /></td>
                    <td className="px-4 py-3">{product.mappingStatus}</td>
                    <td className="px-4 py-3">{fmtDate(product.lastSyncAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {active === 'API Contract' && (
        <div className="space-y-4">
          {(detail.contracts || []).length === 0 ? <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-500">No API contract metadata documented for this member.</div> : detail.contracts.map((contract, idx) => (
            <div key={`${contract.adapter}-${idx}`} className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div><div className="font-bold">{contract.category || contract.adapter}</div><div className="text-sm text-slate-500">{contract.transport}</div></div>
                <span className="text-sm font-semibold text-slate-700">Authentication: {contract.authentication || 'Unknown'}</span>
              </div>
              <div className="mt-4 grid gap-4 lg:grid-cols-3">
                <div><div className="mb-2 text-xs font-bold uppercase text-slate-500">Endpoints</div>{contract.endpoints?.length ? contract.endpoints.map((ep) => <div key={`${ep.method}-${ep.name}`} className="mb-1 rounded bg-slate-50 px-3 py-2 text-sm"><b>{ep.method}</b> {ep.path || 'Not Supported'} <span className="text-slate-500">- {ep.name}</span></div>) : <div className="text-sm text-slate-500">Not Supported</div>}</div>
                <div><div className="mb-2 text-xs font-bold uppercase text-slate-500">Request</div><CodeBlock value={contract.requestSample} /></div>
                <div><div className="mb-2 text-xs font-bold uppercase text-slate-500">Normalized PRIME Response</div><CodeBlock value={contract.normalizedSample} /></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {active === 'Mapping' && (
        <div className="space-y-4">
          {(detail.mappings || []).map((mapping) => (
            <div key={`${mapping.adapter}-${mapping.category}`} className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="mb-3 font-bold">{mapping.category}</div>
              {mapping.fields?.length ? mapping.fields.map((field) => <div key={`${field.memberField}-${field.primeField}`} className="grid grid-cols-[1fr_auto_1fr] gap-3 border-t border-slate-100 py-2 text-sm"><span>{field.memberField}</span><span className="text-slate-400">to</span><span>{field.primeField}</span></div>) : <div className="text-sm text-slate-500">No mapping metadata documented for this adapter.</div>}
            </div>
          ))}
        </div>
      )}

      {active === 'Configuration' && (
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="mb-3 font-bold">Safe Configuration View</div>
          {(detail.contracts || []).flatMap((c) => c.configKeys || []).length === 0 ? <div className="text-sm text-slate-500">No configuration keys documented.</div> : (detail.contracts || []).flatMap((c) => c.configKeys || []).map((cfg) => <div key={cfg.key} className="flex items-center justify-between border-t border-slate-100 py-2 text-sm"><span>{cfg.key}</span><span className="font-semibold">{cfg.status}</span></div>)}
        </div>
      )}

      {active === 'Testing' && (
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          {(detail.tests || []).map((test) => <div key={test.name} className="flex items-center justify-between border-t border-slate-100 py-2 text-sm first:border-t-0"><span>{test.name}</span><span className="font-semibold">{test.status}</span></div>)}
        </div>
      )}

      {active === 'Activity Log' && (
        <div className="rounded-lg border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">No integration activity log available yet.</div>
      )}
    </div>
  );
}

export default function ApiDocumentation() {
  const navigate = useNavigate();
  const { memberCode } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const allowed = normalizeAdminStatus(user) === 'admin_inj';
  const [loading, setLoading] = React.useState(false);
  const [summary, setSummary] = React.useState(null);
  const [integrations, setIntegrations] = React.useState([]);
  const [detail, setDetail] = React.useState(null);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (!allowed) return;
    setLoading(true);
    setError('');
    const requests = [
      channelApi.get('/admin/integrations/summary'),
      channelApi.get('/admin/integrations'),
    ];
    if (memberCode) requests.push(channelApi.get(`/admin/integrations/${memberCode}`));
    Promise.all(requests)
      .then(([sum, list, detailRes]) => {
        setSummary(sum.data?.data?.totals || null);
        setIntegrations(Array.isArray(list.data?.data) ? list.data.data : []);
        setDetail(detailRes?.data?.data || null);
      })
      .catch((e) => setError(e.response?.data?.message || e.message || 'Gagal memuat dokumentasi integrasi.'))
      .finally(() => setLoading(false));
  }, [allowed, memberCode]);

  if (!allowed) {
    return <div className="mx-auto max-w-3xl rounded-lg border border-red-100 bg-white p-8"><h1 className="text-xl font-bold">Akses dibatasi</h1><p className="mt-2 text-slate-600">API Documentation hanya tersedia untuk Admin INJ.</p></div>;
  }

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[.18em] text-slate-500">Internal Tools</div>
          <h1 className="mt-1 text-2xl font-bold text-slate-900">PRIME API & Member Integration</h1>
          <p className="mt-1 text-sm text-slate-600">Dokumentasi teknis integrasi product Member ke PRIME.</p>
        </div>
        {memberCode && <button type="button" onClick={() => navigate('/admin/internal-tools/api-documentation')} className="rounded-md border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">All Integrations</button>}
      </div>

      {error && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}

      {!memberCode ? (
        <IntegrationList integrations={integrations} summary={summary} loading={loading} onView={(code) => navigate(`/admin/internal-tools/api-documentation/${code}`)} />
      ) : !detail ? (
        <div className="rounded-lg border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">{loading ? 'Memuat detail Member...' : 'Integrasi Member tidak ditemukan.'}</div>
      ) : (
        <div className="space-y-5">
          <div className="rounded-lg border border-slate-200 bg-white p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2"><ClipboardList className="h-5 w-5 text-slate-500" /><h2 className="text-xl font-bold text-slate-900">{detail.integration.member.code}</h2></div>
                <p className="mt-1 text-sm text-slate-600">{detail.integration.member.name}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <StatusBadge value={detail.integration.status} />
                <span className="rounded-md border border-slate-200 px-2 py-1 text-[11px] font-bold text-slate-600">{detail.integration.environment || '-'}</span>
                <span className="rounded-md border border-slate-200 px-2 py-1 text-[11px] font-bold text-slate-600">{detail.integration.transport || 'Transport not documented'}</span>
              </div>
            </div>
          </div>
          <DetailTabs detail={detail} selectedProductCode={searchParams.get('product')} />
        </div>
      )}
    </div>
  );
}
